import { supabase } from "../../lib/supabase";
import { WhatsappConnection } from "../../types";
import { normalizeWhatsappStatusBucket } from "../../domain/superadmin/whatsapp";
import {
  canRenderRemoteAvatarUrl,
  isTemporaryRemoteMediaUrl,
  preferPermanentAvatarUrl,
} from "../../utils/failedImageCache";
import { getInstanceProfile } from "../uazapi/instance";

/** Evento: lista de conexões WhatsApp mudou — CRM deve recarregar o cache. */
export const WHATSAPP_CONNECTIONS_UPDATED_EVENT = "crm:whatsapp-connections-updated";

export function notifyWhatsappConnectionsUpdated() {
  window.dispatchEvent(new CustomEvent(WHATSAPP_CONNECTIONS_UPDATED_EVENT));
}

/** Evita heal paralelo da mesma instância em reloads rápidos. */
const healingConnectionAvatars = new Set<string>();

/**
 * Persiste foto da conexão no R2 quando o banco só tem CDN (pps → 403 no browser).
 * Usa getInstanceProfile + media-reupload já existentes.
 */
async function healConnectionAvatar(
  integrationId: string,
  instanceName: string,
  config: Record<string, unknown>,
  seedUrl: string | null,
): Promise<void> {
  if (healingConnectionAvatars.has(instanceName)) return;
  healingConnectionAvatars.add(instanceName);
  try {
    let source = seedUrl;
    try {
      const profile = await getInstanceProfile(instanceName);
      if (profile?.profilePictureUrl) source = profile.profilePictureUrl;
    } catch {
      /* mantém seed */
    }
    if (!source) return;

    let permanent = source;
    if (isTemporaryRemoteMediaUrl(source)) {
      const { data, error } = await supabase.functions.invoke("media-reupload", {
        body: {
          urls: [
            {
              messageId: `avatar-${instanceName}`,
              sourceUrl: source,
              mimeType: "image/jpeg",
              instanceName,
            },
          ],
        },
      });
      if (error) return;
      const uploaded = data?.results?.[0]?.publicUrl as string | undefined;
      if (!uploaded || isTemporaryRemoteMediaUrl(uploaded)) return;
      permanent = uploaded;
    }

    const now = new Date().toISOString();
    await Promise.all([
      supabase
        .from("integrations")
        .update({
          config: { ...config, profile_pic: permanent },
          updated_at: now,
        })
        .eq("id", integrationId),
      supabase
        .from("whatsapp_instances")
        .update({ profile_picture_url: permanent, updated_at: now })
        .eq("instance_name", instanceName),
    ]);
    notifyWhatsappConnectionsUpdated();
  } finally {
    healingConnectionAvatars.delete(instanceName);
  }
}

/**
 * Busca só o WhatsApp Uazapi da organização aberta.
 * A coexistência (whatsapp_cloud) fica fora desta lista.
 * Status: whatsapp_instances quando existir; senão integrations.status.
 * Só devolve foto permanente (R2); CDN dispara heal em background.
 */
export const getWhatsappConnections = async (
  organizationId?: string,
): Promise<WhatsappConnection[]> => {
  try {
    let query = supabase
      .from("integrations")
      .select("*")
      .eq("type", "whatsapp");
    if (organizationId) {
      query = query.eq("organization_id", organizationId);
    }

    const { data, error } = await query;

    if (error) throw error;
    if (!data?.length) return [];

    const instanceNames = data
      .map((conn) => conn.config?.instance_name)
      .filter((name): name is string => typeof name === "string" && !!name);

    const instanceByName = new Map<
      string,
      { status: string; profilePictureUrl: string | null }
    >();
    if (instanceNames.length) {
      const { data: instances } = await supabase
        .from("whatsapp_instances")
        .select("instance_name, status, profile_picture_url")
        .in("instance_name", instanceNames);

      for (const row of instances || []) {
        if (row.instance_name) {
          instanceByName.set(row.instance_name, {
            status: row.status,
            profilePictureUrl: row.profile_picture_url || null,
          });
        }
      }
    }

    return Promise.all(
      data.map(async (conn) => {
        const instanceName = conn.config?.instance_name as string | undefined;
        const instanceRow = instanceName
          ? instanceByName.get(instanceName)
          : undefined;
        const instanceStatus = instanceRow?.status;
        // Preferência: connected se QUALQUER fonte indicar (integrations e whatsapp_instances
        // podem divergir quando o polling reconecta só a integration).
        const integrationBucket = normalizeWhatsappStatusBucket(conn.status);
        const instanceBucket = instanceStatus
          ? normalizeWhatsappStatusBucket(instanceStatus)
          : null;
        const bucket =
          instanceBucket === "connected" || integrationBucket === "connected"
            ? "connected"
            : instanceBucket === "pending" || integrationBucket === "pending"
              ? "pending"
              : instanceBucket || integrationBucket || "disconnected";
        const status: WhatsappConnection["status"] =
          bucket === "connected" ? "Conectado" : "Desconectado";

        // Auto-heal: integration connected + instance stale → alinha a linha da instância.
        if (
          bucket === "connected" &&
          instanceName &&
          instanceBucket &&
          instanceBucket !== "connected"
        ) {
          void supabase
            .from("whatsapp_instances")
            .update({
              status: "connected",
              last_connected_at: new Date().toISOString(),
              disconnect_reason: null,
              updated_at: new Date().toISOString(),
            })
            .eq("instance_name", instanceName);
        }

        // Prefere R2; CDN do polling nunca sobrescreve permanente (preferPermanentAvatarUrl)
        const fromInstance = instanceRow?.profilePictureUrl || null;
        const fromConfig = (conn.config?.profile_pic as string | null) || null;
        const preferred = preferPermanentAvatarUrl(fromInstance, fromConfig);
        const profilePhotoUrl =
          preferred && canRenderRemoteAvatarUrl(preferred) ? preferred : null;

        // Conectado sem R2 → persiste em background (pps não renderiza no browser)
        if (bucket === "connected" && instanceName && !profilePhotoUrl) {
          void healConnectionAvatar(
            conn.id,
            instanceName,
            (conn.config as Record<string, unknown>) || {},
            preferred,
          );
        }

        return {
          id: conn.id,
          number:
            conn.config?.display_number ||
            conn.config?.owner_jid
              ?.replace("@s.whatsapp.net", "")
              .replace(/\D/g, "") ||
            conn.name,
          name: conn.name,
          status,
          profilePhotoUrl,
          instanceName,
        };
      }),
    );
  } catch (error) {
    console.error("Error fetching whatsapp connections:", error);
    return [];
  }
};

/**
 * Lista do chat: UAZAPI mais o número oficial da organização, se existir.
 * A tela de QR continua em getWhatsappConnections.
 */
export async function getChatWhatsappConnections(
  organizationId: string,
): Promise<WhatsappConnection[]> {
  const uazapi = await getWhatsappConnections(organizationId);
  const { data, error } = await supabase
    .from("integrations")
    .select("id, name, status, config")
    .eq("organization_id", organizationId)
    .eq("type", "whatsapp_cloud")
    .maybeSingle();

  if (error || !data) return uazapi;

  const display = data.config?.display_number;
  const number = typeof display === "string" && display ? display : data.name;
  return [
    ...uazapi,
    {
      id: data.id,
      number,
      name: data.name,
      status: data.status === "connected" ? "Conectado" : "Desconectado",
      profilePhotoUrl: null,
      officialApi: true,
    },
  ];
}
